import {
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react"
import { ReactNode, cloneElement, isValidElement, useState } from "react"

import { classNames } from "../../../../lib/classNames"

export interface FloatingTooltipProps {
  trigger: ReactNode
  content: ReactNode
  placement?: Placement
  className?: string
  offsetDistance?: number
  isDisabled?: boolean
  // Opens on focus, tap and Escape too; requires a focusable trigger.
  isInteractive?: boolean
  dismissOnTriggerClick?: boolean
  openDelay?: number
  variant?: "simple" | "complex"
  portal?: boolean
}

export function FloatingTooltip({
  trigger,
  content,
  placement = "bottom-end",
  className = "",
  offsetDistance = 4,
  isDisabled = false,
  isInteractive = false,
  dismissOnTriggerClick = false,
  openDelay = 0,
  variant = "simple",
  portal = false,
}: FloatingTooltipProps) {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [flip(), offset(offsetDistance)],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    delay: { open: openDelay },
    handleClose: safePolygon(),
    move: !dismissOnTriggerClick,
  })
  const focus = useFocus(context, { enabled: isInteractive })
  const click = useClick(context, {
    enabled: isInteractive && !dismissOnTriggerClick,
    ignoreMouse: true,
  })
  const dismiss = useDismiss(context, {
    enabled: isInteractive,
    referencePress: dismissOnTriggerClick,
    referencePressEvent: "click",
  })
  const role = useRole(context, { role: "tooltip", enabled: isInteractive })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  if (isDisabled) return trigger

  const tooltip = (
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      className={classNames(
        variant === "simple"
          ? "bg-neutral-bold-default color-inverse"
          : "bg-v2-background-elevation-overlay border-v2-border-default color-default border",
        "text-body shadow-v2-overlay rounded-overlay z-20 whitespace-pre-line px-3 py-2",
        className
      )}
      data-testid="floating-tooltip-content"
      {...getFloatingProps()}
    >
      {content}
    </div>
  )

  return (
    <>
      {isInteractive && isValidElement<Record<string, unknown>>(trigger) ? (
        // On the trigger itself, so aria-describedby lands where focus does.
        cloneElement(trigger, {
          ...getReferenceProps(trigger.props),
          ref: refs.setReference,
          "data-testid": trigger.props["data-testid"] ?? "floating-tooltip",
        })
      ) : (
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          data-testid="floating-tooltip"
        >
          {trigger}
        </div>
      )}
      {isOpen &&
        (portal ? (
          <FloatingPortal root={document.body}>{tooltip}</FloatingPortal>
        ) : (
          tooltip
        ))}
    </>
  )
}
